import { SearchOutlined } from '@ant-design/icons'
import { Empty, Input, InputRef, Modal, ModalProps } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import { useArrowTabIndex } from '../arrow-tabindex'
import { useSearch } from '../hooks'
import { UserSearchList } from '../user/components/user-search-list'
import { CampaignSearchList } from '../campaign/components/campaign-search-list'
import { SearchResponse } from '../api'
import { useTranslation } from 'react-i18next'
import { CandidateSearchList } from '../candidate/components/candidate-search-list'

export type SearchProps = { onCancel: () => void } & Omit<ModalProps, 'footer' | 'centered' | 'styles' | 'closable'>

export function Search({ onCancel, open, ...rest }: SearchProps) {
    const searchRef = useRef<InputRef>(null)
    const [query, setQuery] = useState('')
    const { t } = useTranslation()
    const search = useSearch(query)
    useArrowTabIndex('.search li')

    const hasResult =
        Object.keys(search?.data || {}).filter((key) => {
            if (!search.data) {
                return false
            }

            const data = search.data[key as keyof SearchResponse]

            if (typeof data === 'undefined') {
                return false
            }

            return data.length > 0
        }).length > 0

    useEffect(() => {
        if (open) {
            setTimeout(() => {
                searchRef.current!.focus({
                    cursor: 'all',
                })
            }, 200)
        }
    }, [open])

    return (
        <Modal
            centered
            className="search"
            styles={{
                content: {
                    padding: 0,
                },
                footer: {
                    display: 'none',
                },
            }}
            onCancel={onCancel}
            open={open}
            footer={<></>}
            closable={false}
            {...rest}
        >
            <Input
                ref={searchRef}
                size="large"
                value={query}
                onChange={(event) => {
                    setQuery(event.target.value)
                }}
                className={query ? 'rounded-b-none' : ''}
                prefix={<SearchOutlined />}
                placeholder="Search..."
            />
            {search.isSuccess && (
                <>
                    {!hasResult && (
                        <Empty
                            className="min-h-32 my-12"
                            description={t('No search result')}
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                    )}
                    {search.data.users && search.data.users.length > 0 && (
                        <UserSearchList
                            users={search.data.users}
                            onCancel={onCancel}
                        />
                    )}
                    {search.data.campaigns && search.data.campaigns.length > 0 && (
                        <CampaignSearchList
                            campaigns={search.data.campaigns}
                            onCancel={onCancel}
                        />
                    )}
                    {search.data.candidates && search.data.candidates.length > 0 && (
                        <CandidateSearchList
                            candidates={search.data.candidates}
                            onCancel={onCancel}
                        />
                    )}
                </>
            )}
        </Modal>
    )
}
